// The poneva spark — the animated four-pointed star brand mark, ported from
// apps/assistant-platform (guide-spark.tsx + spark-motion.css). States:
//   wake     one-shot bounce-in (page load)
//   idle     slow breathing + twinkling
//   thinking orbiting rays + sway + fast twinkle (assistant is working)
//   done     single twinkle (answer finished)
// Colors default to the brand orange on our dark theme; override via props.

import { useEffect, useState, type FC } from 'react';

export type SparkState = 'none' | 'idle' | 'wake' | 'thinking' | 'done';

const OUTER_PATH =
  'M32 7C34.3 16.5 47.5 29.7 57 32C47.5 34.3 34.3 47.5 32 57C29.7 47.5 16.5 34.3 7 32C16.5 29.7 29.7 16.5 32 7Z';
const INNER_PATH =
  'M32 22.5C33 26.5 37.5 31 41.5 32C37.5 33 33 37.5 32 41.5C31 37.5 26.5 33 22.5 32C26.5 31 31 26.5 32 22.5Z';
const SMALL_INNER_PATH =
  'M32 20C33.3 25.4 36.6 30.7 44 32C36.6 33.3 33.3 38.6 32 44C30.7 38.6 27.4 33.3 20 32C27.4 30.7 30.7 25.4 32 20Z';

const RAYS: Array<[number, number, number, number]> = [
  [47, 17, 51.5, 12.5],
  [17, 17, 12.5, 12.5],
  [47, 47, 51.5, 51.5],
  [17, 47, 12.5, 51.5],
];

export const Spark: FC<{
  size: number;
  state?: SparkState;
  withRays?: boolean;
  body?: string;
  hole?: string;
  ray?: string;
  className?: string;
}> = ({
  size,
  state = 'none',
  withRays = true,
  body = '#ED6C1F',
  hole = 'var(--sidebar, #161b22)',
  ray = '#8F8474',
  className,
}) => {
  const stateClass = state === 'none' ? '' : ` spark--${state}`;
  const innerPath = size <= 20 ? SMALL_INNER_PATH : INNER_PATH;
  return (
    <svg
      className={`spark${stateClass}${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      {withRays ? (
        <g className="rays">
          {RAYS.map(([x1, y1, x2, y2], i) => (
            <line
              key={i}
              className="ray"
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={ray}
              strokeWidth="5"
              strokeLinecap="round"
            />
          ))}
        </g>
      ) : null}
      <g className="mark">
        <path d={OUTER_PATH} fill={body} stroke={body} strokeWidth="4" strokeLinejoin="round" />
        <path className="inner" d={innerPath} fill={hole} />
      </g>
    </svg>
  );
};

/**
 * The greeting spark: one-shot wake on mount, then idle breathing. Shared by
 * the auth front door, the sidebar lockup, and the empty-thread welcome so the
 * brand moment is defined once.
 */
export const WakingSpark: FC<{ size: number; className?: string }> = ({ size, className }) => {
  const [state, setState] = useState<'wake' | 'idle'>('wake');
  useEffect(() => {
    const t = setTimeout(() => setState('idle'), 1000);
    return () => clearTimeout(t);
  }, []);
  return <Spark size={size} state={state} className={className} />;
};
